import { useCallback, useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useTable, useGlobalFilter, useSortBy, usePagination } from "react-table";
import { useAuth0 } from "@auth0/auth0-react";

import { GetFarmDetails, GetDecisions, GetCurrentDecision, ApplyDecision, ScanMiners, useCheckScan, useCheckDecision, GetMiners } from "api/farm";

import ImportMinersModal from "components/Miners/ImportMinersModal";
import Pagination from "components/Util/Pagination";
import MinerProfile from "components/Miners/MinerProfile";
import Modal from "components/Util/Modal";

import Spinner from "components/svg/Spinner";
import NoAccess from "components/Util/NoAccess";

export default function Miners() {
  const { user } = useAuth0();

  const [selectedRows, setSelectedRows] = useState([]);
  const [farmUrl, setFarmUrl] = useState("");
  const [farmKey, setFarmKey] = useState("");
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [miners, setMiners] = useState([]);
  const [refetchInterval, setRefetchInterval] = useState(0);
  const [isMessageOpen, setIsMessageOpen] = useState(false);
  const [decisionInterval, setDecisionInterval] = useState(0);
  const [decisions, setDecisions] = useState([]);
  const [selectedDecision, setSelectedDecision] = useState("");
  const [decisionInUse, setDecisionInUse] = useState("");

  const [farmName] = useOutletContext<any>();

  const updateMiners = useCallback(() => {
    if (farmUrl !== "" && farmUrl !== null && farmKey && farmKey !== "") {
      GetMiners(farmUrl, farmKey)
        .then((data: any) => setMiners(data))
        .catch(() => setMiners([]));
    }
  }, [farmUrl, farmKey]);

  useEffect(() => {
    if (farmName !== "") {
      GetFarmDetails(user["https://hasura.io/jwt/claims"]["x-hasura-user-id"]).then((details: any) => {
        if (typeof details !== "undefined") {
          setFarmUrl(details.farm_url);
          setFarmKey(details.farm_token);
        }
      });
    }
  }, [user, farmName]);

  useEffect(() => {
    if (farmUrl !== "" && farmUrl !== null && farmKey !== "") {
      updateMiners();
      GetDecisions(farmUrl, farmKey).then((data: any) => setDecisions(data));
      GetCurrentDecision(farmUrl, farmKey).then((data: any) => {
        setSelectedDecision(`${data.current_decision}`);
        setDecisionInUse(`${data.current_decision}`);
      });
    }
  }, [farmUrl, farmKey, updateMiners]);

  const closeImportModal = () => {
    setIsImportOpen(false);
  };

  const openImportModal = () => {
    setIsImportOpen(true);
  };

  const importMiners = (ip: string) => {
    ScanMiners(farmUrl, farmKey, ip).then(() => setRefetchInterval(2000));
    setRefetchInterval(10);
    closeImportModal();
  };

  const { data: scanStatus } = useCheckScan(farmUrl, farmKey, refetchInterval);
  const { data: decisionStatus } = useCheckDecision(farmUrl, farmKey, decisionInterval);

  useEffect(() => {
    if (refetchInterval !== 10 && (scanStatus === "scan complete" || scanStatus === "please start a scan")) {
      updateMiners();
      setRefetchInterval(0);
      document.body.classList.remove('disable-settings');
    } else if (scanStatus === "loading") {
      setRefetchInterval(1000);
      document.body.classList.add('disable-settings');
    } else if (refetchInterval !== 10) {
      setRefetchInterval(5000);
      document.body.classList.add('disable-settings');
    }
  }, [scanStatus, updateMiners, refetchInterval]);

  useEffect(() => {
    if (decisionStatus === "ready") {
      setDecisionInterval(0);
      document.body.classList.remove('disable-settings');
    } else if (decisionStatus === "loading") {
      setDecisionInterval(1000);
      document.body.classList.add('disable-settings');
    } else {
      setDecisionInterval(5000);
      document.body.classList.add('disable-settings');
    }
  }, [decisionStatus]);

  const toggleSelect = useCallback(
    (rowData: any, selectAll?: boolean) => {
      if (selectAll && selectedRows.length) {
        setSelectedRows([]);
      } else if (selectAll) {
        setSelectedRows(rowData);
      } else {
        let foundIndex = selectedRows.findIndex((row) => row.ip === rowData.ip);

        if (foundIndex !== -1) {
          const newSelectedRows = [...selectedRows];
          newSelectedRows.splice(foundIndex, 1);
          setSelectedRows(newSelectedRows);
        } else {
          setSelectedRows([...selectedRows, rowData]);
        }
      }
    },
    [selectedRows]
  );

  const columns = useMemo<any[]>(
    () => [
      {
        Header: ({ data }: any) => (
          <input
            type="checkbox"
            className="ml-4 hidden"
            title="Select all"
            defaultChecked={selectedRows.length && selectedRows.length === data.length}
            onClick={() => toggleSelect(data, true)}
          />
        ),
        accessor: "ip",
        id: "select",
      },
      {
        Header: "Ip",
        accessor: "ip",
      },
      {
        Header: "Mac Address",
        accessor: "macAddress",
      },
      {
        Header: "Miner Type",
        accessor: "minertype",
      },
      {
        Header: decisions.length ? (
          <div className="show-spinner">
            <select
              className="bg-white border border-transparent w-full rounded-sm px-0 py-2"
              onChange={({ target: { value } }) => setSelectedDecision(value)}
              value={selectedDecision}
            >
              {decisions.map((entry) => (
                <option value={entry.id} key={entry.id} className={decisionInUse === entry.id ? "font-bold" : ""}>
                  {entry.name}
                </option>
              ))}
            </select>
            <Spinner className="block mx-auto h-5 w-5 text-gray-300" />
          </div>
        ) : (
          <div className="text-red-500 font-normal">No profile available</div>
        ),
        accessor: "decisions",
      },
      {
        Header: decisions.length ? (
          <div className="show-spinner">
            <button
              type="button"
              className="bg-white border-slate-200 w-full px-2 py-2 font-medium text-xs hover:bg-gray-50 text-blue-700"
              onClick={() => {
                ApplyDecision(farmUrl, farmKey, selectedDecision).then(() => setDecisionInterval(1000));
              }}
              disabled={selectedDecision === ""}
            >
              Apply Profile
            </button>
            <Spinner className="block mx-auto h-5 w-5 text-gray-400" />
          </div>
        ) : (
          ""
        ),
        accessor: "switchtype",
        align: "center",
      },
    ],
    [selectedRows, toggleSelect, decisions, selectedDecision, decisionInUse, farmUrl, farmKey]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    page,
    prepareRow,
    setGlobalFilter,
    gotoPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
  }: any = useTable(
    {
      columns,
      data: miners,
      initialState: { pageSize: 10, sortBy: [{ id: "minertype", desc: false }] },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  return (
    <div className="p-6">
      {(farmUrl === "") || (farmUrl === null) ?
        <NoAccess/>
        :
        <section className=" pt-5 sm:overflow-hidden">
          <div className="flex border-b pb-3 justify-between items-end mb-1 border-gray-200">
            <input
              type="search"
              className="form-input px-3 border-solid placeholder-gray-300 border-gray-200 rounded-sm text-sm py-2 w-56 focus:border-transparent focus:ring-transparent"
              placeholder="Search"
              value={globalFilter || ""}
              onChange={(e) => {
                setGlobalFilter(e.target.value || undefined);
              }}
            />
            <div className="flex items-center show-spinner">
              <button
                  type="button"
                  className="form-input inline-flex items-center border border-transparent leading-4 font-medium shadow-sm text-white focus:outline-none bg-blue-600 hover:bg-blue-700 rounded-sm px-4 py-2 text-sm"
                  onClick={openImportModal}
                >
                  Import Miners
              </button>
              <Spinner className="block mx-auto h-5 w-5 text-gray-400" />
              <ImportMinersModal isOpen={isImportOpen} onClose={closeImportModal} onSubmit={importMiners} />
            </div>
          </div>
          <div className="flex flex-col">
            <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                <div className="overflow-hidden">
                  <Modal isOpen={isMessageOpen} onClose={() => setIsMessageOpen(false)} title={`Success`}></Modal>
                  <table {...getTableProps()} className="mt-3 w-full table-auto border-collapse bg-white rounded-sm border-b-2">
                    <thead>
                      {headerGroups.map((headerGroup: any) => (
                        <tr {...headerGroup.getHeaderGroupProps()}>
                          {headerGroup.headers.map((column: any) => (
                            <th
                              {...column.getHeaderProps()}
                              align="left"
                              className="border-slate-100 uppercase text-sm text-gray-800 font-semibold py-6 px-2 border-b-2"
                            >
                              {column.render("Header")}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                      {miners.length ? (
                        page.map((row: any, i: number) => {
                          prepareRow(row);
                          return (
                            <MinerProfile
                              key={i}
                              miner={row.original}
                              farmUrl={farmUrl}
                              farmKey={farmKey}
                              showPresets={decisions.length}
                              selectedDecision={selectedDecision}
                              isSelected={selectedRows.findIndex((miner) => miner.ip === row.original.ip) !== -1}
                              onClick={() => toggleSelect(row.original)}
                              onUpdate={updateMiners}
                            />
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan={10} align="center" className="p-20 text-xs">
                            No Miners available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  <Pagination pageIndex={pageIndex} pageSize={pageSize} setPageSize={setPageSize} gotoPage={gotoPage} rowCount={rows.length} extend />
                </div>
              </div>
            </div>
          </div>
        </section>
      }
    </div>
  );
}
